import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Edit3,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { PayrollEntry, CoreEmployee, EmployeeGroup, PayItemValue } from '../types';
import Select from 'react-select';
import Tooltip from './Tooltip';
import { formatPayItemValue, parsePayItemInput } from '../utils/hoursFormat';

// Format option label with Tooltip for long labels
const FormatOptionWithTooltip: React.FC<{
  option: { label: string; value: string };
}> = ({ option }) => (
  <Tooltip content={option.label} delay={300}>
    <span className="block truncate">{option.label}</span>
  </Tooltip>
);
const formatOptionLabelWithTooltip = (option: { label: string; value: string }) => (
  <FormatOptionWithTooltip option={option} />
);

interface TemplateColumn {
  payItemId: number;
  title: string;
  valueType: string;
  displayOrder: number;
}

interface ExpandablePayrollRowProps {
  // Mode selector
  mode?: 'legacy' | 'dynamic';

  // Legacy props (mode === 'legacy')
  entry?: PayrollEntry;
  hasErrors?: boolean;
  validationErrors?: any;
  onChange?: (field: string, value: string) => void;
  onHighlight?: (field: string, value: string) => void;
  isFieldHighlighted?: (field: string, value: string) => boolean;
  getCurrentOCRResult?: () => any;

  // Dynamic props (mode === 'dynamic')
  group?: EmployeeGroup;
  templateColumns?: TemplateColumn[];
  onCellChange?: (itemId: string, value: string, isHoursMinutes: boolean) => void;
  onEmployeeAssign?: (
    employeeId: string | null,
    employeeName?: string | null,
    employeeNumber?: string | null
  ) => void;
  onEmployeeTypeChange?: (type: string) => void;

  // Common props
  isExpanded: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onRemove: () => void;
  coreEmployees?: CoreEmployee[];
  usedEmployeeIds?: Set<string>;
  isDuplicate?: boolean;
}

// Compact input for table cells (numeric)
const CompactInput: React.FC<{
  value: number;
  field: string;
  onChange: (field: string, value: string) => void;
}> = ({ value, field, onChange }) => {
  const [localValue, setLocalValue] = useState<string>(value ? String(value) : '');
  const [hasError, setHasError] = useState(false);

  // Sync local value when prop changes (e.g., from parent state update)
  useEffect(() => {
    setLocalValue(value ? String(value) : '');
    setHasError(false);
  }, [value]);

  const isValidNumber = (val: string): boolean => {
    if (val === '' || val === '.') return true; // Allow empty and typing decimal
    return /^[0-9]*\.?[0-9]*$/.test(val) && !isNaN(parseFloat(val));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);

    // Check for invalid characters (letters, symbols)
    if (val !== '' && !/^[0-9]*\.?[0-9]*$/.test(val)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  const handleBlur = () => {
    if (localValue === '' || localValue === '.') {
      onChange(field, String(0));
      setLocalValue('');
      setHasError(false);
    } else if (isValidNumber(localValue)) {
      onChange(field, String(parseFloat(localValue) || 0));
      setHasError(false);
    } else {
      // Invalid input - revert to previous value
      setLocalValue(value ? String(value) : '');
      setHasError(false);
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`w-16 px-2 py-1 text-sm text-center border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          hasError ? 'border-red-500 bg-red-50' : 'border-neutral-300'
        }`}
        onClick={(e) => e.stopPropagation()}
      />
      {hasError && (
        <div className="absolute top-full left-0 mt-1 text-xs text-red-600 whitespace-nowrap z-10">
          ⚠️ Please enter a valid number (e.g., 40 or 3.5)
        </div>
      )}
    </div>
  );
};

// Text input for string fields (employee name, ID, etc.)
const TextInput: React.FC<{
  value: string;
  field: string;
  onChange: (field: string, value: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, field, onChange, placeholder, className = '' }) => {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(field, e.target.value)}
      className={`px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

// Employee ID dropdown selector - replaces the ID text input
// Saves the Core API `id` field (e.g., 827035) to the database
// Displays `employeeNumber` (e.g., "5") in the dropdown to help users identify employees
// Uses react-select for searchable dropdown with employees sorted A-Z
const EmployeeIdDropdown: React.FC<{
  employees: CoreEmployee[];
  selectedId: string;
  onChange: (field: string, value: string) => void;
  usedEmployeeIds?: Set<string>;
}> = ({ employees, selectedId, onChange, usedEmployeeIds }) => {
  const hasEmployees = employees && employees.length > 0;

  const employeeOptions = hasEmployees
    ? [...employees]
        .sort((a, b) => {
          const nameA = (
            a.preferredName || `${a.firstName} ${a.lastName}`
          ).toLowerCase();
          const nameB = (
            b.preferredName || `${b.firstName} ${b.lastName}`
          ).toLowerCase();
          return nameA.localeCompare(nameB);
        })
        .map((emp) => {
          const name = emp.preferredName || `${emp.firstName} ${emp.lastName}`;
          const idStr = emp.id.toString();
          const isUsed = usedEmployeeIds?.has(idStr) ?? false;
          return {
            value: idStr,
            label: isUsed
              ? `${name} (employee id: ${emp.employeeNumber}) — already assigned`
              : `${name} (employee id: ${emp.employeeNumber})`,
            isDisabled: isUsed,
          };
        })
    : [];

  const selectedOption =
    employeeOptions.find((opt) => opt.value === selectedId) || null;

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ width: '288px' }}>
      <Select
        value={selectedOption}
        onChange={(option) => onChange('employeeId', option?.value || '')}
        options={employeeOptions}
        isSearchable={true}
        isClearable={true}
        placeholder="Search or select..."
        noOptionsMessage={() =>
          hasEmployees ? 'No match found' : 'No employees loaded'
        }
        isDisabled={!hasEmployees}
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabelWithTooltip}
        styles={{
          control: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            minHeight: '32px',
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
            '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
            backgroundColor: hasEmployees ? 'white' : '#fef3c7',
            borderRadius: '0.25rem',
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
          }),
          singleValue: (base) => ({
            ...base,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
            fontSize: '0.875rem',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          option: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            padding: '8px 12px',
            backgroundColor: state.isDisabled
              ? '#f5f5f5'
              : state.isSelected
                ? '#3b82f6'
                : state.isFocused
                  ? '#eff6ff'
                  : 'white',
            color: state.isDisabled
              ? '#9ca3af'
              : state.isSelected
                ? 'white'
                : '#374151',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
          }),
        }}
      />
    </div>
  );
};

// Input field component with label for expanded section
const LabeledInput: React.FC<{
  label: string;
  value: number;
  field: string;
  onChange: (field: string, value: string) => void;
  prefix?: string;
  suffix?: string;
}> = ({ label, value, field, onChange, prefix = '', suffix = '' }) => {
  const [localValue, setLocalValue] = useState<string>(value ? String(value) : '');
  const [hasError, setHasError] = useState(false);

  // Sync local value when prop changes
  useEffect(() => {
    setLocalValue(value ? String(value) : '');
    setHasError(false);
  }, [value]);

  const isValidNumber = (val: string): boolean => {
    if (val === '' || val === '.') return true;
    return /^[0-9]*\.?[0-9]*$/.test(val) && !isNaN(parseFloat(val));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);

    if (val !== '' && !/^[0-9]*\.?[0-9]*$/.test(val)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  };

  const handleBlur = () => {
    if (localValue === '' || localValue === '.') {
      onChange(field, String(0));
      setLocalValue('');
      setHasError(false);
    } else if (isValidNumber(localValue)) {
      onChange(field, String(parseFloat(localValue) || 0));
      setHasError(false);
    } else {
      setLocalValue(value ? String(value) : '');
      setHasError(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-neutral-500 font-medium">{label}</label>
      <div className="flex items-center gap-1">
        {prefix && <span className="text-neutral-600 text-sm">{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-20 px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            hasError ? 'border-red-500 bg-red-50' : 'border-neutral-300'
          }`}
        />
        {suffix && <span className="text-neutral-500 text-sm">{suffix}</span>}
      </div>
      {hasError && (
        <span className="text-xs text-red-600">
          ⚠️ Please enter a valid number (e.g., 40 or 3.5)
        </span>
      )}
    </div>
  );
};

const ExpandablePayrollRow: React.FC<ExpandablePayrollRowProps> = (props) => {
  const {
    mode = 'legacy',
    isExpanded,
    canEdit,
    onToggle,
    onRemove,
    coreEmployees,
    usedEmployeeIds,
    isDuplicate,
  } = props;

  // Dynamic mode — render template-driven columns
  if (mode === 'dynamic' && props.group && props.templateColumns) {
    return (
      <DynamicPayrollRow
        group={props.group}
        templateColumns={props.templateColumns}
        isExpanded={isExpanded}
        canEdit={canEdit}
        onToggle={onToggle}
        onCellChange={props.onCellChange!}
        onEmployeeAssign={props.onEmployeeAssign!}
        onEmployeeTypeChange={props.onEmployeeTypeChange!}
        onRemove={onRemove}
        coreEmployees={coreEmployees}
        usedEmployeeIds={usedEmployeeIds}
        isDuplicate={isDuplicate}
      />
    );
  }

  // Legacy mode
  const entry = props.entry!;
  const onChange = props.onChange!;
  const hasErrors = props.hasErrors ?? false;
  const isSalaried = entry.employeeType === 'salary';
  const isDeleted = entry.deletedByAgent ?? false;

  // Hour values
  const regularHours = entry.regularHours || 0;
  const overtimeHours = entry.overtimeHours || 0;
  const sickHours = entry.sickHours || 0;
  const vacationHours = entry.vacationHours || 0;
  const holidayHours = entry.holidayHours || 0;
  const personalHours = entry.personalHours || 0;

  // Calculated values
  const ptoHours = sickHours + vacationHours + holidayHours + personalHours;
  const totalHours = regularHours + overtimeHours + ptoHours;

  const bonusEarnings = entry.bonusEarnings || 0;
  const hasAdditionalEarnings = bonusEarnings > 0;

  // Status determination
  const getStatus = () => {
    if (isDeleted) {
      return {
        label: 'Deleted',
        color: 'text-red-700',
        bg: 'bg-red-100',
        icon: Trash2,
      };
    }
    if (hasErrors) {
      return {
        label: 'Needs Review',
        color: 'text-red-600',
        bg: 'bg-red-50',
        icon: AlertTriangle,
      };
    }
    if (entry.isEdited) {
      return {
        label: 'Modified',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: Edit3,
      };
    }
    if (!entry.employeeId || entry.employeeId === '—') {
      return {
        label: 'New Hire',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: AlertTriangle,
      };
    }
    return {
      label: 'Matched',
      color: 'text-green-600',
      bg: 'bg-green-50',
      icon: Check,
    };
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  return (
    <>
      {/* Main Row */}
      <tr
        className={`border-b border-neutral-200 cursor-pointer transition-colors ${
          isDeleted
            ? 'bg-red-50 hover:bg-red-100'
            : isDuplicate
              ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-400'
              : isExpanded
                ? 'bg-white hover:bg-neutral-50'
                : 'hover:bg-neutral-50'
        }`}
      >
        {/* Employee Name & ID */}
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-neutral-400 hover:text-neutral-600"
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            <div className="flex flex-col gap-1">
              {/* Employee Name - text input */}
              <div className="flex items-center gap-2">
                {canEdit ? (
                  <TextInput
                    value={entry.employeeName}
                    field="employeeName"
                    onChange={onChange}
                    placeholder="Employee name"
                    className="font-medium text-neutral-900 w-40"
                  />
                ) : (
                  <span className="font-medium text-neutral-900">
                    {entry.employeeName}
                  </span>
                )}
                {isSalaried && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 uppercase tracking-wide">
                    Salary
                  </span>
                )}
              </div>
              {/* Employee ID - dropdown selector (replaces text input) */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-neutral-500">ID:</span>
                {canEdit ? (
                  <EmployeeIdDropdown
                    employees={coreEmployees || []}
                    selectedId={entry.employeeId || ''}
                    onChange={onChange}
                    usedEmployeeIds={usedEmployeeIds}
                  />
                ) : (
                  <span className="text-xs text-neutral-500">
                    {entry.employeeId || '—'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Hours columns - editable for both hourly and salaried employees */}
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput
              value={regularHours}
              field="regularHours"
              onChange={onChange}
            />
          ) : (
            <span className="text-neutral-700">{regularHours.toFixed(2)}</span>
          )}
        </td>
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput
              value={overtimeHours}
              field="overtimeHours"
              onChange={onChange}
            />
          ) : (
            <span className="text-neutral-700">{overtimeHours.toFixed(2)}</span>
          )}
        </td>
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput value={sickHours} field="sickHours" onChange={onChange} />
          ) : (
            <span className="text-neutral-700">{sickHours.toFixed(2)}</span>
          )}
        </td>
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput
              value={vacationHours}
              field="vacationHours"
              onChange={onChange}
            />
          ) : (
            <span className="text-neutral-700">{vacationHours.toFixed(2)}</span>
          )}
        </td>
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput
              value={holidayHours}
              field="holidayHours"
              onChange={onChange}
            />
          ) : (
            <span className="text-neutral-700">{holidayHours.toFixed(2)}</span>
          )}
        </td>
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canEdit ? (
            <CompactInput
              value={personalHours}
              field="personalHours"
              onChange={onChange}
            />
          ) : (
            <span className="text-neutral-700">{personalHours.toFixed(2)}</span>
          )}
        </td>

        {/* Total Hours */}
        <td className="py-3 px-3 text-center font-semibold text-neutral-900">
          {totalHours.toFixed(2)}
        </td>
      </tr>

      {/* Expanded Detail Row - Status & Additional Earnings */}
      {isExpanded && (
        <tr
          className={`border-b border-neutral-200 ${isDeleted ? 'bg-red-50' : isDuplicate ? 'bg-red-50' : ''}`}
        >
          <td colSpan={9} className="p-0">
            <div
              className={`px-6 py-4 ml-8 border-l-2 ${
                isDeleted
                  ? 'bg-red-50 border-red-400'
                  : isDuplicate
                    ? 'bg-red-50 border-red-400'
                    : 'bg-neutral-50 border-blue-400'
              }`}
            >
              {/* Status & Employee Type */}
              <div className="mb-4 flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                    Status:
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${status.bg} ${status.color}`}
                  >
                    <StatusIcon size={12} />
                    {status.label}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                    Type:
                  </span>
                  {canEdit ? (
                    <select
                      value={entry.employeeType || 'hourly'}
                      onChange={(e) => onChange('employeeType', e.target.value)}
                      className="px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="salary">Salary</option>
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        isSalaried
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {isSalaried ? 'Salary' : 'Hourly'}
                    </span>
                  )}
                </div>

                {/* Spacer to push delete button to the right */}
                <div className="flex-1" />

                {/* Delete/Restore Button */}
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      isDeleted
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {isDeleted ? (
                      <>
                        <RotateCcw size={14} />
                        Restore Employee
                      </>
                    ) : (
                      <>
                        <Trash2 size={14} />
                        Delete Employee
                      </>
                    )}
                  </button>
                )}
              </div>

              {isSalaried ? (
                /* Salary Section */
                <div>
                  <h4 className="text-xs font-bold text-neutral-700 uppercase tracking-wide mb-3">
                    Additional Earnings
                  </h4>
                  <div className="flex flex-wrap gap-4">
                    <LabeledInput
                      label="Bonus"
                      value={bonusEarnings}
                      field="bonusEarnings"
                      onChange={onChange}
                      prefix="$"
                    />
                  </div>
                </div>
              ) : (
                /* Additional Earnings Section */
                <div>
                  <h4 className="text-xs font-bold text-neutral-700 uppercase tracking-wide mb-3">
                    Additional Earnings
                  </h4>
                  <div className="flex flex-wrap gap-4">
                    <LabeledInput
                      label="Bonus"
                      value={bonusEarnings}
                      field="bonusEarnings"
                      onChange={onChange}
                      prefix="$"
                    />
                  </div>
                  {!hasAdditionalEarnings && (
                    <p className="text-sm text-neutral-500 mt-2">
                      No additional earnings for this employee.
                    </p>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Dynamic (template-driven) payroll row
// ═══════════════════════════════════════════════════════════════════════════

interface DynamicPayrollRowProps {
  group: EmployeeGroup;
  templateColumns: {
    payItemId: number;
    title: string;
    valueType: string;
    displayOrder: number;
  }[];
  isExpanded: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onCellChange: (itemId: string, value: string, isHoursMinutes: boolean) => void;
  onEmployeeAssign: (
    employeeId: string | null,
    employeeName?: string | null,
    employeeNumber?: string | null
  ) => void;
  onEmployeeTypeChange: (type: string) => void;
  onRemove: () => void;
  coreEmployees?: CoreEmployee[];
  usedEmployeeIds?: Set<string>;
  isDuplicate?: boolean;
}

const DynamicCellInput: React.FC<{
  item: PayItemValue;
  onCellChange: (itemId: string, value: string, isHoursMinutes: boolean) => void;
}> = ({ item, onCellChange }) => {
  // Show the stored value in HH:MM form when the cell is flagged.
  // For dollar cells the flag is never set, so this reduces to the raw value.
  const initialDisplay = formatPayItemValue(item.value, item.isHoursMinutes);
  const [localValue, setLocalValue] = useState(initialDisplay);

  useEffect(() => {
    setLocalValue(formatPayItemValue(item.value, item.isHoursMinutes));
  }, [item.value, item.isHoursMinutes]);

  const handleBlur = () => {
    const trimmed = localValue.trim();
    if (trimmed === '' || trimmed === '.') {
      onCellChange(item.id, '', false);
      setLocalValue('');
      return;
    }

    // Dollar cells keep the old behaviour — numeric parse, reject colons —
    // because HH:MM doesn't apply to money.
    if (item.valueType === 'dollars') {
      const num = parseFloat(trimmed);
      if (!isNaN(num)) {
        onCellChange(item.id, String(num), false);
      } else {
        setLocalValue(formatPayItemValue(item.value, item.isHoursMinutes));
      }
      return;
    }

    // Hour cells: accept either ``9:31`` or ``9.31``. Parser swaps the colon
    // for a period when present and flips the flag accordingly.
    const parsed = parsePayItemInput(trimmed);
    if (parsed.value === null) {
      onCellChange(item.id, '', false);
      setLocalValue('');
      return;
    }

    // Guard: if the result isn't even numeric after swap, revert to the
    // previous stored value rather than send garbage to the server.
    if (isNaN(parseFloat(parsed.value))) {
      setLocalValue(formatPayItemValue(item.value, item.isHoursMinutes));
      return;
    }

    onCellChange(item.id, parsed.value, parsed.isHoursMinutes);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      className="w-20 px-2 py-1 text-sm text-center border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const DynamicPayrollRow: React.FC<DynamicPayrollRowProps> = ({
  group,
  templateColumns,
  isExpanded,
  canEdit,
  onToggle,
  onCellChange,
  onEmployeeAssign,
  onEmployeeTypeChange,
  onRemove,
  coreEmployees,
  usedEmployeeIds,
  isDuplicate,
}) => {
  const isSalaried = group.employeeType === 'salary';

  const getStatus = () => {
    if (!group.employeeId || group.employeeId.trim() === '') {
      return {
        label: 'No ID',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: AlertTriangle,
      };
    }
    return {
      label: 'Matched',
      color: 'text-green-600',
      bg: 'bg-green-50',
      icon: Check,
    };
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  // Render cells by iterating the column headers (not the employee's
  // payItems) so dual-mode pay items — which yield two columns sharing
  // the same payItemId but differing valueType — line up with the headers.
  // Falling back through (payItemId, valueType) → payItemId-only handles
  // the rare case where an old row predates dual-mode seeding.
  const findItemForColumn = (
    payItemId: number,
    valueType: string
  ): PayItemValue | undefined => {
    return (
      group.payItems.find(
        (p) => p.payItemId === payItemId && p.valueType === valueType
      ) || group.payItems.find((p) => p.payItemId === payItemId)
    );
  };

  return (
    <>
      <tr
        className={`cursor-pointer transition-colors ${
          isDuplicate
            ? 'bg-red-50 hover:bg-red-100'
            : isExpanded
              ? 'bg-white hover:bg-neutral-50'
              : 'hover:bg-neutral-50'
        }`}
      >
        {/* Employee Name & ID (frozen column) */}
        <td
          className={`py-3 px-4 border-b border-neutral-200 sticky left-0 z-10 min-w-[140px] ${isDuplicate ? 'bg-red-50 border-l-4 border-l-red-400' : 'bg-white'}`}
        >
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-neutral-400 hover:text-neutral-600"
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-neutral-900">
                  {group.employeeName || 'New Employee'}
                </span>
                {isSalaried && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 uppercase tracking-wide">
                    Salary
                  </span>
                )}
              </div>
              <div
                className="flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-xs text-neutral-500">ID:</span>
                {canEdit ? (
                  <EmployeeIdDropdown
                    employees={coreEmployees || []}
                    selectedId={group.employeeId || ''}
                    onChange={(_field, value) => {
                      const emp = coreEmployees?.find((e) => e.id.toString() === value);
                      onEmployeeAssign(
                        value || null,
                        null,
                        emp ? emp.employeeNumber : null
                      );
                    }}
                    usedEmployeeIds={usedEmployeeIds}
                  />
                ) : (
                  <span className="text-xs text-neutral-500">
                    {group.employeeId || '—'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Dynamic pay item cells — iterate columns (not group.payItems)
            so dual-mode columns line up with the headers. */}
        {templateColumns.map((col) => {
          const item = findItemForColumn(col.payItemId, col.valueType);
          const colKey = `${col.payItemId}:${col.valueType}`;
          if (!item) {
            return (
              <td
                key={colKey}
                className="py-3 px-2 text-center border-b border-neutral-200 min-w-[90px] text-neutral-400"
                onClick={(e) => e.stopPropagation()}
              >
                —
              </td>
            );
          }
          return (
            <td
              key={colKey}
              className="py-3 px-2 text-center border-b border-neutral-200 min-w-[90px]"
              onClick={(e) => e.stopPropagation()}
            >
              {canEdit ? (
                <DynamicCellInput item={item} onCellChange={onCellChange} />
              ) : (
                <span className="text-neutral-700">
                  {item.value
                    ? item.valueType === 'dollars'
                      ? `$${parseFloat(item.value).toFixed(2)}`
                      : item.isHoursMinutes
                        ? formatPayItemValue(item.value, true)
                        : parseFloat(item.value).toFixed(2)
                    : '—'}
                </span>
              )}
            </td>
          );
        })}
      </tr>

      {/* Expanded detail row */}
      {isExpanded && (
        <tr className={`${isDuplicate ? 'bg-red-50' : ''}`}>
          <td
            colSpan={templateColumns.length + 1}
            className="p-0 border-b border-neutral-200"
          >
            <div
              className={`px-6 py-4 ml-8 border-l-2 ${isDuplicate ? 'bg-red-50 border-red-400' : 'bg-neutral-50 border-blue-400'}`}
            >
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                    Status:
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${status.bg} ${status.color}`}
                  >
                    <StatusIcon size={12} />
                    {status.label}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                    Type:
                  </span>
                  {canEdit ? (
                    <select
                      value={group.employeeType || 'hourly'}
                      onChange={(e) => onEmployeeTypeChange(e.target.value)}
                      className="px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="salary">Salary</option>
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${isSalaried ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}
                    >
                      {isSalaried ? 'Salary' : 'Hourly'}
                    </span>
                  )}
                </div>

                <div className="flex-1" />

                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors bg-red-100 text-red-700 hover:bg-red-200"
                  >
                    <Trash2 size={14} />
                    Delete Employee
                  </button>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default ExpandablePayrollRow;
